import { lazy, Suspense } from "react";

import { useMe } from "@/api/hooks";
import { Spinner } from "@/components/ui/misc";
import { useAuth } from "@/lib/auth";
import type { User } from "@/lib/types";
import { NotFoundPage } from "./not-found";

// The console is its own chunk: normal users never download it.
const AdminApp = lazy(() => import("@/features/admin/admin-app").then((m) => ({ default: m.AdminApp })));

export type AdminAccess = "loading" | "allowed" | "denied";

/**
 * Whether to render the admin console. Decided from a fresh GET /me (not the
 * cached session user), and anything but an explicit `true` is a denial. The
 * API enforces this on every admin request regardless.
 */
export function adminAccess(me: {
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  data?: Pick<User, "is_platform_admin">;
}): AdminAccess {
  if (me.isPending) return me.isError ? "denied" : "loading";
  if (me.data?.is_platform_admin === true) return "allowed";
  // a cached "not an admin" may be stale (just promoted): wait for the fresh answer
  if (me.isFetching) return "loading";

  return "denied";
}

function FullPageSpinner() {
  return (
    <div className="flex h-dvh items-center justify-center">
      <Spinner />
    </div>
  );
}

/** /admin/*: the console for platform admins, the normal not-found page for everyone else. */
export function AdminGate() {
  const { status } = useAuth();
  const me = useMe({ fresh: true, enabled: status === "authenticated" });
  const access = adminAccess(me);

  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") return <NotFoundPage />;
  if (access === "loading") return <FullPageSpinner />;
  if (access === "denied") return <NotFoundPage />;

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <AdminApp notFound={<NotFoundPage embedded />} />
    </Suspense>
  );
}
